import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/admin/Dashboard';
import Employees from './pages/admin/Employees';
import AdminAttendance from './pages/admin/Attendance';
import LeaveSettings from './pages/admin/LeaveSettings';
import LeaveApprovals from './pages/admin/LeaveApprovals';
import Placeholder from './pages/admin/Placeholder';
import Expenses from './pages/admin/Expenses';
import Reports from './pages/admin/Reports';
import Policies from './pages/admin/Policies';
import Evaluations from './pages/admin/Evaluations';
import EmployeeHome from './pages/portal/EmployeeHome';
import EmployeeAttendance from './pages/portal/Attendance';
import EmployeeLeave from './pages/portal/Leave';
import Reimbursements from './pages/portal/Reimbursements';
import MyProfile from './pages/portal/MyProfile';
import PortalPlaceholder from './pages/portal/Placeholder';
import Conversations from './pages/Conversations';
import './App.css';
import 'leaflet/dist/leaflet.css';

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563EB]"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && requiredRole !== 'any' && user?.role !== requiredRole) {
    return <Navigate to={user?.role === 'admin' ? '/admin/dashboard' : '/portal'} replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route - Redirect if authenticated
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563EB]"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={user?.role === 'admin' ? '/admin/dashboard' : '/portal'} replace />;
  }

  return children;
};

// Root redirect based on role
const RootRedirect = () => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563EB]"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={user?.role === 'admin' ? '/admin/dashboard' : '/portal'} replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Root */}
          <Route path="/" element={<RootRedirect />} />

          {/* Public Routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute requiredRole="admin">
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/employees"
            element={
              <ProtectedRoute requiredRole="admin">
                <Employees />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/attendance"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminAttendance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/leave"
            element={
              <ProtectedRoute requiredRole="admin">
                <LeaveApprovals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/leave-settings"
            element={
              <ProtectedRoute requiredRole="admin">
                <LeaveSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/expenses"
            element={
              <ProtectedRoute requiredRole="admin">
                <Expenses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/policies"
            element={
              <ProtectedRoute requiredRole="admin">
                <Policies />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/evaluations"
            element={
              <ProtectedRoute requiredRole="admin">
                <Evaluations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute requiredRole="admin">
                <Reports />
              </ProtectedRoute>
            }
          />

          {/* Employee Portal Routes (accessible by both roles) */}
          <Route
            path="/portal"
            element={
              <ProtectedRoute requiredRole="any">
                <EmployeeHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal/attendance"
            element={
              <ProtectedRoute requiredRole="any">
                <EmployeeAttendance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal/leave"
            element={
              <ProtectedRoute requiredRole="any">
                <EmployeeLeave />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal/expenses"
            element={
              <ProtectedRoute requiredRole="any">
                <Reimbursements />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal/profile"
            element={
              <ProtectedRoute requiredRole="any">
                <MyProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/conversations"
            element={
              <ProtectedRoute requiredRole="any">
                <Conversations />
              </ProtectedRoute>
            }
          />

          {/* Catch all - redirect to root */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
